import { Box, Button, Chip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import type { AvailableOption } from '@/types';
import { DoorGlyph } from './doorGlyphs';
import { refineGlyph } from './techGlyphs';
import { ProductActions } from './ProductActions';
import { ElectricalPlanButton } from './ElectricalPlanButton';

// Glifi TECNOLOGIA senza vista impianto elettrico (Temperatura, SmartHome), come nel legacy.
const NO_ELECTRICAL_GLYPHS = new Set(['smartHome', 'termostato']);

interface BaseConfigCardProps {
  /** Opzioni base/default (OGN=0) del substep = la configurazione di partenza (capitolato). */
  baseOptions: AvailableOption[];
  /** true quando la base è lo stato effettivo corrente (substep risposto e nessuna variante attiva). */
  confirmed: boolean;
  /** true quando il substep ha già una risposta (base o variante). */
  answered: boolean;
  isBusy: boolean;
  /** Conferma la configurazione di partenza (salva l'insieme delle opzioni base). */
  onConfirm: () => void;
  /** Fase corrente (per mostrare la planimetria elettrico solo in TECNOLOGIA). */
  phaseKey: string | null;
  /** Id configurazione (per caricare la vista elettrico). */
  configurationId: number;
}

// Card "LA CONFIGURAZIONE DI PARTENZA" (legacy SelectionCard): centrata, fondo grigio #F0EFEE,
// bordo verde quando confermata.
const CardRoot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'confirmed',
})<{ confirmed: boolean }>(({ theme, confirmed }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(2.75, 2.5),
  backgroundColor: '#F0EFEE',
  border: `${confirmed ? 2 : 1}px solid`,
  borderColor: confirmed ? theme.palette.secondary.main : '#BEBCBB',
  borderRadius: theme.shape.borderRadius,
}));

/**
 * Card "LA CONFIGURAZIONE DI PARTENZA" (colonna sinistra, sotto la planimetria): rappresenta le
 * opzioni base/default del substep con il pulsante CONFERMA ⇄ CONFERMATO, come nel legacy.
 */
export function BaseConfigCard({
  baseOptions,
  confirmed,
  answered,
  isBusy,
  onConfirm,
  phaseKey,
  configurationId,
}: BaseConfigCardProps) {
  const hasBase = baseOptions.length > 0;
  const descriptions = baseOptions
    .map((o) => o.description)
    .filter((d): d is string => d != null && d !== '');
  // Glifo della base (prima opzione base con glyph), raffinato coi suoi dati. Assente → nessun render.
  const baseOption = baseOptions.find((o) => o.glyph) ?? null;
  const baseGlyph = baseOption ? refineGlyph(baseOption) : null;
  // Prodotto di catalogo della base (prima opzione base con prodotto) per Foto/Scheda Tecnica.
  const baseProduct = baseOptions.find((o) => o.product)?.product ?? null;
  // Vista impianto elettrico: solo in TECNOLOGIA, esclusi Temperatura/SmartHome (come il legacy).
  const showElectrical =
    phaseKey === 'Tecnologia' && (baseGlyph == null || !NO_ELECTRICAL_GLYPHS.has(baseGlyph));

  return (
    <CardRoot confirmed={confirmed} sx={{ mt: 2 }}>
      {baseGlyph && <DoorGlyph name={baseGlyph} height={80} label="Configurazione di partenza" />}

      {/* Titolo (uppercase 15px) + descrizione (miller corsivo), centrati come il legacy SelectionCard. */}
      <Typography sx={{ fontSize: 15, textTransform: 'uppercase', fontWeight: 600, mb: 0.25 }}>
        La configurazione di partenza
      </Typography>
      <Typography
        sx={{ fontFamily: '"miller", serif', fontStyle: 'italic', fontSize: 15, color: 'text.secondary', mb: 0.5 }}
      >
        {descriptions.length > 0
          ? descriptions.join(' · ')
          : 'Soluzione inclusa nel capitolato, senza costi aggiuntivi.'}
      </Typography>

      <Typography sx={{ fontSize: 13, color: 'secondary.main', fontWeight: 500 }}>
        Incluso nel capitolato
      </Typography>

      {/* Foto / Scheda Tecnica del prodotto base (asset di catalogo dal BE). */}
      <ProductActions product={baseProduct} name="Configurazione di partenza" />

      {/* Vista impianto elettrico (solo TECNOLOGIA, esclusi Temperatura/SmartHome). */}
      {showElectrical && <ElectricalPlanButton configurationId={configurationId} />}

      {confirmed && (
        <Chip
          size="small"
          color="secondary"
          icon={<CheckCircleIcon fontSize="small" />}
          label="Confermato"
          sx={{ fontWeight: 500 }}
        />
      )}

      <Button
        variant={confirmed ? 'outlined' : 'contained'}
        color="secondary"
        disabled={!hasBase || isBusy || confirmed}
        onClick={onConfirm}
        sx={{ mt: 0.5 }}
        aria-label={
          confirmed
            ? 'Configurazione di partenza confermata'
            : 'Conferma la configurazione di partenza'
        }
      >
        {confirmed ? 'Confermato' : answered ? 'Ripristina la base' : 'Conferma'}
      </Button>
    </CardRoot>
  );
}
